import { useCallback, useMemo, useState } from "react"
import { Flex, Modal } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import CustomDrawer from "ui/Drawer"
import { useDevice } from "hooks/useDevice"
import GenerateForm from "./GenerateForm"
import GeneratedSlotsList from "./GeneratedSlotsList"
import { useUndoRedo } from "./useUndoRedo"
import { findOverlappingDepartures } from "./utils"
import styles from "./ScheduleExperienceModal.module.css"

export const ScheduleExperienceModal = ({ open, onClose, onConfirm, experienceName, durationMinutes, isMultiDay, timeZone, existingSlots }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const { state: generatedSlots, set, undo, redo, reset, canUndo, canRedo } = useUndoRedo([])
  // { added, skipped } from the last confirm — shown inline; cleared on any list change
  const [confirmResult, setConfirmResult] = useState(null)

  // Multi-day only: two departures out at the same time is allowed, but worth a warning.
  // Checked against the already-scheduled start times too, not just this batch.
  const overlaps = useMemo(
    () => {
      if (!isMultiDay || generatedSlots.length === 0) return []
      // Generated duplicates of a scheduled time are skipped on confirm — don't count them as overlaps.
      const existingLabels = new Set(existingSlots.map((slot) => slot.label))
      const newSlots = generatedSlots.filter((slot) => !existingLabels.has(slot.label))
      return findOverlappingDepartures([...existingSlots, ...newSlots], durationMinutes)
    },
    [isMultiDay, existingSlots, generatedSlots, durationMinutes]
  )

  // Returns { added } so the form can tell the user when everything was a duplicate.
  const handleGenerate = useCallback(
    (newSlots) => {
      setConfirmResult(null)
      const existingLabels = new Set(generatedSlots.map((slot) => slot.label))
      const unique = newSlots.filter((slot) => !existingLabels.has(slot.label))
      // Skip the no-op set — it would push an identical entry onto the undo stack
      if (unique.length > 0) set([...generatedSlots, ...unique])
      return { added: unique.length }
    },
    [generatedSlots, set]
  )

  const handleRemoveSlot = useCallback(
    (id) => {
      setConfirmResult(null)
      set(generatedSlots.filter((s) => s.id !== id))
    },
    [generatedSlots, set]
  )

  const handleClearAll = useCallback(() => {
    setConfirmResult(null)
    set([])
  }, [set])

  const handleUndo = useCallback(() => {
    setConfirmResult(null)
    undo()
  }, [undo])

  const handleRedo = useCallback(() => {
    setConfirmResult(null)
    redo()
  }, [redo])

  // Anything added → close; all-duplicates → stay open with the inline
  // warning and the pending list intact for editing.
  const handleConfirm = useCallback(() => {
    const result = onConfirm(generatedSlots)
    if (result?.added > 0) {
      reset()
      setConfirmResult(null)
      onClose()
    } else {
      setConfirmResult(result)
    }
  }, [generatedSlots, onConfirm, onClose, reset])

  const handleClose = () => {
    reset()
    setConfirmResult(null)
    onClose()
  }

  const modalTitle = t("experiences.ticketed_schedule.modal_title")

  const content = (
    <Flex gap={24} align="flex-start" className={styles.modalBody}>
      <GenerateForm
        experienceName={experienceName}
        durationMinutes={durationMinutes}
        isMultiDay={isMultiDay}
        timeZone={timeZone}
        onGenerate={handleGenerate}
      />
      <GeneratedSlotsList
        slots={generatedSlots}
        onRemoveSlot={handleRemoveSlot}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onClearAll={handleClearAll}
        canUndo={canUndo}
        canRedo={canRedo}
        onConfirm={handleConfirm}
        confirmResult={confirmResult}
        overlaps={overlaps}
      />
    </Flex>
  )

  if (isMobile) {
    return (
      <CustomDrawer open={open} setOpen={() => handleClose()} title={modalTitle} destroyOnClose>
        {content}
      </CustomDrawer>
    )
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={modalTitle}
      footer={null}
      width={1400}
      centered
      destroyOnClose
      className={styles.modal}
    >
      {content}
    </Modal>
  )
}

ScheduleExperienceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  experienceName: PropTypes.string,
  durationMinutes: PropTypes.number,
  isMultiDay: PropTypes.bool,
  timeZone: PropTypes.string,
  existingSlots: PropTypes.arrayOf(PropTypes.shape({ starts_at: PropTypes.string, label: PropTypes.string }))
}

ScheduleExperienceModal.defaultProps = {
  experienceName: "",
  durationMinutes: 120,
  isMultiDay: false,
  timeZone: undefined,
  existingSlots: []
}
